import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Table, Button, Select, Space, Modal, Input, InputNumber,
  message, Tag, Switch,
} from "antd";
import {
  ReloadOutlined, DollarOutlined, EyeOutlined, CreditCardOutlined,
  FileTextOutlined, ClusterOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency } from "../../../utils/formatters";
import { formatOrderNo, orderDocNo } from "../../../shared/lib/documentNumbers";
import { readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { fixedStart } from "../../../i18";
import { useCustomersCatalog } from "../../../hooks/useCatalogData";
import usePermissions from "../../../hooks/usePermissions";
import { PageHeader, StatCard, T, cardStyle } from "../../../shared/ui/PageShell";
import DateRangeFilter from "../../../shared/ui/DateRangeFilter";
import useTablePagination from "../../../shared/ui/useTablePagination";
import { resolveDatePreset } from "../../../shared/lib/dateFilterPresets";

const debtAge = (createdAt) => {
  if (!createdAt) return { days: 0, color: "#16a34a", background: "#dcfce7", border: "#86efac" };
  const days = Math.max(0, dayjs().startOf("day").diff(dayjs(createdAt).startOf("day"), "day"));
  if (days <= 3) return { days, color: "#15803d", background: "#dcfce7", border: "#86efac" };
  if (days <= 7) return { days, color: "#a16207", background: "#fef9c3", border: "#fde047" };
  return { days, color: "#b91c1c", background: "#fee2e2", border: "#fca5a5" };
};

const cashierOf = (item, usersById = {}) => {
  const named =
    item.cashier
    || item.cashier_name
    || item.creator?.name
    || (typeof item.accountant === "string" ? item.accountant : item.accountant?.name)
    || item.user?.name
    || item.created_by_name
    || "";
  if (named) return named;
  const id = item.cashier_id ?? item.created_by ?? item.user_id ?? item.user?.id ?? item.creator?.id;
  if (id != null && usersById[String(id)]) return usersById[String(id)];
  return "";
};

const UnpaidSales = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canPay = hasPermission("customers.payment");
  const { data: customers } = useCustomersCatalog();
  const mainCode = readCachedSystemConfig()?.currency_code || "IQD";
  const { pagination } = useTablePagination();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [range, setRange] = useState(() => resolveDatePreset("this_month", { withTime: true }));
  const [customerId, setCustomerId] = useState(undefined);
  const [cashierId, setCashierId] = useState(undefined);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [showAdjustments, setShowAdjustments] = useState(false);
  const [adjustmentRows, setAdjustmentRows] = useState([]);
  const [adjustmentsLoading, setAdjustmentsLoading] = useState(false);

  const [payOpen, setPayOpen] = useState(false);
  const [payRows, setPayRows] = useState([]);
  const [payAmount, setPayAmount] = useState(0);
  const [payNote, setPayNote] = useState("");
  const [paySaving, setPaySaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/orders");
      const list = Array.isArray(res.data) ? res.data : [];
      const unpaid = list
        .map((item) => {
          const total = parseFloat(item.total_price || 0);
          const paid = parseFloat(item.amount_paid || 0);
          const remaining = parseFloat(
            item.outstanding_balance != null ? item.outstanding_balance : Math.max(0, total - paid)
          );
          const cust = item.customer || {};
          const cid = item.cashier_id ?? item.created_by ?? item.user_id ?? item.creator?.id ?? null;
          return {
            id: item.id,
            document_no: item.document_no || formatOrderNo(item.id, item.created_at),
            created_at: item.created_at,
            customer_id: cust.id || item.customer_id || null,
            customer_name: cust.name || item.customer_name || t("noCustomer", "No customer"),
            cashier_id: cid,
            // Orders payload already includes creator — do not call /users (needs users.index)
            cashier: cashierOf(item) || "—",
            total,
            paid,
            remaining: Math.max(0, remaining),
            source: item.source || "shop",
            status: item.status,
            debt_age: debtAge(item.created_at),
          };
        })
        .filter((r) => r.remaining > 0.0001 && r.status !== "cancelled");
      setRows(unpaid);
      setSelectedKeys([]);
    } catch (e) {
      message.error(e?.response?.data?.message || t("unpaidSalesLoadFailed", "Failed to load unpaid sales"));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const loadAdjustments = useCallback(async () => {
    setAdjustmentsLoading(true);
    try {
      const res = await apiClient.get("/customers/unpaid-adjustments");
      const list = Array.isArray(res.data) ? res.data : [];
      setAdjustmentRows(list.map((item) => {
        const amount = parseFloat(item.amount || 0);
        return {
          id: `adj-${item.customer_id}`,
          document_no: t("unpaidSalesAdjustment", "Adjustment"),
          created_at: item.created_at,
          customer_id: item.customer_id,
          customer_name: item.customer_name || t("noCustomer", "No customer"),
          cashier_id: null,
          cashier: "—",
          total: amount,
          paid: 0,
          remaining: Math.max(0, amount),
          source: "adjustment",
          status: "confirmed",
          debt_age: debtAge(item.created_at),
          isAdjustment: true,
          note: item.note || "",
        };
      }));
    } catch (e) {
      message.error(e?.response?.data?.message || t("unpaidSalesLoadFailed", "Failed to load unpaid sales"));
      setAdjustmentRows([]);
    } finally {
      setAdjustmentsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (showAdjustments) loadAdjustments();
  }, [showAdjustments, loadAdjustments]);

  const cashierOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      if (r.cashier_id == null) return;
      const id = String(r.cashier_id);
      if (!map.has(id)) {
        map.set(id, r.cashier && r.cashier !== "—" ? r.cashier : `#${id}`);
      }
    });
    return [...map.entries()]
      .sort((a, b) => String(a[1]).localeCompare(String(b[1])))
      .map(([value, label]) => ({ value: Number(value) || value, label }));
  }, [rows]);

  const customerOptions = useMemo(
    () => (customers || []).map((c) => ({ value: c.id, label: c.name || `#${c.id}` })),
    [customers]
  );

  const filtered = useMemo(() => {
    const start = range?.[0] ? dayjs(range[0]) : null;
    const end = range?.[1] ? dayjs(range[1]) : null;
    const combined = showAdjustments ? [...rows, ...adjustmentRows] : rows;
    return combined.filter((r) => {
      const d = r.created_at ? dayjs(r.created_at) : null;
      if (start && d && d.isBefore(start)) return false;
      if (end && d && d.isAfter(end)) return false;
      if (customerId != null && String(r.customer_id) !== String(customerId)) return false;
      if (cashierId != null && String(r.cashier_id) !== String(cashierId)) return false;
      return true;
    });
  }, [rows, adjustmentRows, showAdjustments, range, customerId, cashierId]);

  const stats = useMemo(() => {
    const count = filtered.length;
    const outstanding = filtered.reduce((s, r) => s + r.remaining, 0);
    const totalAmount = filtered.reduce((s, r) => s + r.total, 0);
    const avg = count ? outstanding / count : 0;
    return { count, outstanding, totalAmount, avg };
  }, [filtered]);

  const selectedRows = useMemo(
    () => filtered.filter((r) => selectedKeys.includes(r.id)),
    [filtered, selectedKeys]
  );
  const selectedTotal = selectedRows.reduce((s, r) => s + r.remaining, 0);
  const selectedCustomerIds = [...new Set(selectedRows.map((r) => r.customer_id).filter(Boolean))];
  const bulkSameCustomer = selectedRows.length > 0 && selectedCustomerIds.length === 1;

  const openPayFor = (saleRows) => {
    const list = Array.isArray(saleRows) ? saleRows : [saleRows];
    if (!list.length) return;
    const ids = [...new Set(list.map((r) => r.customer_id).filter(Boolean))];
    if (ids.length !== 1) {
      message.warning(t("unpaidSalesSameCustomer", "Select invoices for one customer only"));
      return;
    }
    const rem = list.reduce((s, r) => s + r.remaining, 0);
    setPayRows(list);
    setPayAmount(rem);
    setPayNote("");
    setPayOpen(true);
  };

  const submitPay = async () => {
    const customer = payRows[0]?.customer_id;
    const amt = parseFloat(payAmount || 0);
    if (!customer) {
      message.error(t("unpaidSalesNeedsCustomer", "These sales need a customer to record payment"));
      return;
    }
    if (!(amt > 0)) {
      message.error(t("vendorsPaymentInvalid", "Enter a valid amount"));
      return;
    }
    // Selected unpaid remaining is the debt before this payment.
    const owedBefore = Number(payRows.reduce((s, r) => {
      const rem = parseFloat(
        (r.remaining ?? (parseFloat(r.total_price || 0) - parseFloat(r.amount_paid || 0))) || 0
      );
      return s + Math.max(0, rem);
    }, 0).toFixed(2));
    const remainingAfter = Math.round(Math.max(0, owedBefore - amt) * 100) / 100;
    setPaySaving(true);
    try {
      const fd = new FormData();
      fd.append("id", String(customer));
      fd.append("amount", String(amt));
      fd.append("payment_method", "cash");
      if (payNote) fd.append("sender", payNote);
      const res = await apiClient.post(`/customers/pay/${customer}`, fd);
      message.success(t("vendorsPaymentSuccess", "Payment recorded"));
      setPayOpen(false);
      setSelectedKeys([]);
      await load();
      if (showAdjustments) await loadAdjustments();
      const paymentId = res.data?.payment?.id;
      if (paymentId) {
        const q = new URLSearchParams({
          old: String(owedBefore),
          paid: String(amt),
          remain: String(remainingAfter),
          name: payRows[0]?.customer_name || "",
        });
        window.open(
          `/customers/${customer}/receipts/${paymentId}?${q.toString()}`,
          "_blank",
          "noopener,noreferrer"
        );
      }
    } catch (e) {
      message.error(e?.response?.data?.message || t("vendorsPaymentFailed", "Payment failed"));
    } finally {
      setPaySaving(false);
    }
  };

  const columns = [
    {
      title: <b>{t("actions", "Actions")}</b>,
      key: "actions",
      width: 200,
      fixed: fixedStart(i18n.language),
      render: (_, r) => (
        <Space wrap size={6}>
          {!r.isAdjustment && (
            <Button size="small" icon={<EyeOutlined />} style={{ borderRadius: 7 }} onClick={() => navigate(`/orders/${r.id}/view`)}>
              {t("unpaidSalesViewDetails", "View Details")}
            </Button>
          )}
          {canPay && r.customer_id && (
            <Button size="small" type="primary" icon={<DollarOutlined />} style={{ borderRadius: 7 }} onClick={() => openPayFor([r])}>
              {t("pay", "Pay")}
            </Button>
          )}
        </Space>
      ),
    },
    {
      title: <b>{t("orderNo", "Bill #")}</b>,
      dataIndex: "document_no",
      key: "document_no",
      width: 140,
      render: (v, r) =>
        r.isAdjustment ? (
          <Tag color="purple" style={{ margin: 0, borderRadius: 6, fontWeight: 700 }}>
            {v}
          </Tag>
        ) : (
          <Link to={`/orders/${r.id}/view`} style={{ fontWeight: 700, fontFamily: "monospace", fontSize: 12 }}>
            {v || orderDocNo(r)}
          </Link>
        ),
    },
    {
      title: <b>{t("date", "Date")}</b>,
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      render: (v) => (
        <span style={{ color: T.muted, fontSize: 12 }}>
          {v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—"}
        </span>
      ),
    },
    {
      title: <b>{t("unpaidSalesAge", "Age")}</b>,
      dataIndex: "debt_age",
      key: "debt_age",
      width: 100,
      sorter: (a, b) => a.debt_age.days - b.debt_age.days,
      render: (age) => (
        <Tag
          style={{
            margin: 0,
            color: age.color,
            background: "#ffffffcc",
            borderColor: age.border,
            borderRadius: 7,
            fontWeight: 700,
          }}
        >
          {t("unpaidSalesDaysOld", "{{count}} days", { count: age.days })}
        </Tag>
      ),
    },
    {
      title: <b>{t("customer", "Customer")}</b>,
      dataIndex: "customer_name",
      key: "customer_name",
      render: (v, r) =>
        r.customer_id ? (
          <Link to={`/customers/${r.customer_id}`} style={{ fontWeight: 600 }}>{v}</Link>
        ) : (
          <span style={{ color: T.sub }}>{v}</span>
        ),
    },
    {
      title: <b>{t("cashier", "Cashier")}</b>,
      dataIndex: "cashier",
      key: "cashier",
      render: (v) => <Tag style={{ borderRadius: 6 }}>{v || "—"}</Tag>,
    },
    {
      title: <b>{t("total", "Total")}</b>,
      dataIndex: "total",
      key: "total",
      align: "right",
      render: (v) => <span style={{ fontWeight: 600 }}>{formatCurrency(v, mainCode)}</span>,
    },
    {
      title: <b>{t("paid", "Paid")}</b>,
      dataIndex: "paid",
      key: "paid",
      align: "right",
      render: (v) => <span style={{ color: T.green, fontWeight: 600 }}>{formatCurrency(v, mainCode)}</span>,
    },
    {
      title: <b>{t("unpaidSalesRemaining", "Remaining")}</b>,
      dataIndex: "remaining",
      key: "remaining",
      align: "right",
      render: (v, r) => (
        <b style={{ color: r.debt_age.color }}>{formatCurrency(v, mainCode)}</b>
      ),
    },
  ];

  return (
    <div style={{ padding: "clamp(12px, 2.5vw, 24px)", background: T.bg, minHeight: "100%" }}>
      <PageHeader
        breadcrumb={[t("sidebarSectionReports", "Reports"), t("unpaidSalesTitle", "Unpaid Sales")]}
        title={t("unpaidSalesTitle", "Unpaid Sales")}
        subtitle={t("unpaidSalesHint", "Credit sales with outstanding balance — filter, review, and collect payment")}
        action={(
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ borderRadius: 9 }}>
            {t("currencyTotalsRefresh", "Refresh")}
          </Button>
        )}
      />

      <div className="velan-stats-row">
        <StatCard
          icon={<FileTextOutlined style={{ color: "#2563eb" }} />}
          label={t("unpaidSalesCount", "Total Unpaid Sales")}
          value={stats.count}
          iconBg="#dbeafe"
        />
        <StatCard
          icon={<DollarOutlined style={{ color: "#e11d48" }} />}
          label={t("unpaidSalesOutstanding", "Total Outstanding")}
          value={formatCurrency(stats.outstanding, mainCode)}
          iconBg="#fee2e2"
          valueColor={T.red}
        />
        <StatCard
          icon={<ClusterOutlined style={{ color: "#d97706" }} />}
          label={t("unpaidSalesAverage", "Average Per Sale")}
          value={formatCurrency(stats.avg, mainCode)}
          iconBg="#fef3c7"
          valueColor="#d97706"
        />
        <StatCard
          icon={<CreditCardOutlined style={{ color: "#7c3aed" }} />}
          label={t("unpaidSalesTotalAmount", "Total Sales Amount")}
          value={formatCurrency(stats.totalAmount, mainCode)}
          iconBg="#ede9fe"
        />
      </div>

      <div style={{ ...cardStyle, padding: "16px 20px", marginBottom: 16 }}>
        <Space wrap size={[12, 12]} align="end">
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, marginBottom: 4 }}>{t("dateRange", "Date range")}</div>
            <DateRangeFilter
              value={range}
              onChange={(v) => setRange(v || [null, null])}
              showTime
              allowClear
              size="small"
            />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, marginBottom: 4 }}>{t("customer", "Customer")}</div>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t("customer", "Customer")}
              value={customerId}
              onChange={setCustomerId}
              options={customerOptions}
              style={{ minWidth: 200 }}
            />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, marginBottom: 4 }}>{t("cashier", "Cashier")}</div>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t("allCashiers", "All cashiers")}
              value={cashierId}
              onChange={setCashierId}
              options={cashierOptions}
              style={{ minWidth: 160 }}
            />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, marginBottom: 4 }}>
              {t("unpaidSalesShowAdjustments", "Include debt without a sale")}
            </div>
            <Switch
              checked={showAdjustments}
              onChange={setShowAdjustments}
              loading={adjustmentsLoading}
            />
          </div>
        </Space>
      </div>

      {canPay && selectedRows.length > 0 && (
        <div
          style={{
            ...cardStyle,
            padding: "12px 16px",
            marginBottom: 12,
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "center",
            justifyContent: "space-between",
            borderColor: "#93c5fd",
            background: "#eff6ff",
          }}
        >
          <div style={{ fontWeight: 600, color: T.text }}>
            <span>{selectedRows.length} {t("unpaidSalesSelected", "invoices selected")}</span>
            <span style={{ marginInlineStart: 12, color: T.red }}>
              {t("total", "Total")}: {formatCurrency(selectedTotal, mainCode)}
            </span>
          </div>
          <Space>
            <Button onClick={() => setSelectedKeys([])} style={{ borderRadius: 8 }}>{t("clear", "Clear")}</Button>
            <Button
              type="primary"
              disabled={!bulkSameCustomer}
              onClick={() => openPayFor(selectedRows)}
              style={{ borderRadius: 8 }}
            >
              {t("unpaidSalesPaySelected", "Pay Selected")}
            </Button>
          </Space>
          {!bulkSameCustomer && (
            <div style={{ width: "100%", fontSize: 12, color: "#b45309" }}>
              {t("unpaidSalesSameCustomer", "Select invoices for one customer only")}
            </div>
          )}
        </div>
      )}

      <div style={{ ...cardStyle }}>
        <div style={{ padding: "14px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>{t("unpaidSalesTitle", "Unpaid Sales")}</span>
          <Space wrap size={6}>
            <Tag color="green">{t("unpaidSalesAgeGreen", "1–3 days")}</Tag>
            <Tag color="gold">{t("unpaidSalesAgeYellow", "4–7 days")}</Tag>
            <Tag color="red">{t("unpaidSalesAgeRed", "8+ days")}</Tag>
          </Space>
        </div>
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={filtered}
          pagination={pagination}
          locale={{ emptyText: t("unpaidSalesEmpty", "No unpaid sales found") }}
          scroll={{ x: 980 }}
          onRow={(r) => ({
            style: {
              background: r.debt_age.background,
              borderInlineStart: `3px solid ${r.debt_age.border}`,
            },
          })}
          rowSelection={canPay ? {
            selectedRowKeys: selectedKeys,
            onChange: setSelectedKeys,
            getCheckboxProps: (r) => ({ disabled: !r.customer_id }),
          } : undefined}
        />
      </div>

      <Modal
        open={payOpen}
        title={payRows.length > 1
          ? t("unpaidSalesPaySelectedTitle", "Pay Selected Invoices")
          : t("unpaidSalesPayTitle", "Collect payment")}
        onCancel={() => setPayOpen(false)}
        onOk={submitPay}
        okText={t("pay", "Pay")}
        confirmLoading={paySaving}
        destroyOnHidden
        okButtonProps={{ disabled: !(parseFloat(payAmount) > 0) }}
      >
        <div style={{ marginBottom: 12, fontSize: 13, color: T.sub }}>
          {t("customer", "Customer")}: <b style={{ color: T.text }}>{payRows[0]?.customer_name}</b>
          {" · "}
          {payRows.length} {t("unpaidSalesInvoices", "invoice(s)")}
        </div>
        <div style={{ marginBottom: 10, padding: 10, background: "#f8fafc", borderRadius: 8, fontSize: 13 }}>
          {payRows.slice(0, 8).map((r) => (
            <div key={r.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "3px 0" }}>
              <span style={{ fontFamily: "monospace", fontSize: 12 }}>{r.document_no || orderDocNo(r)}</span>
              <b style={{ color: T.red }}>{formatCurrency(r.remaining, mainCode)}</b>
            </div>
          ))}
          {payRows.length > 8 && <div style={{ color: T.muted }}>… +{payRows.length - 8}</div>}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, paddingTop: 8, borderTop: `1px solid ${T.border}`, fontWeight: 700 }}>
            <span>{t("unpaidSalesRemaining", "Remaining")}</span>
            <span style={{ color: T.red }}>{formatCurrency(payRows.reduce((s, r) => s + r.remaining, 0), mainCode)}</span>
          </div>
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t("amount", "Amount")}</div>
        <InputNumber
          min={0}
          value={payAmount}
          onChange={(v) => setPayAmount(v)}
          style={{ width: "100%", marginBottom: 12 }}
          controls={false}
        />
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t("note", "Note")}</div>
        <Input
          value={payNote}
          onChange={(e) => setPayNote(e.target.value)}
          placeholder={t("unpaidSalesPayNotePlaceholder", "e.g. Partial payment, settlement")}
        />
      </Modal>
    </div>
  );
};

export default UnpaidSales;
